import React, { useEffect, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import dayjs from "dayjs";
import { ArrowDropDownIcon, PickersLayout } from "@mui/x-date-pickers";
import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";
import useThemeColor from "../../utils/useTheme";
import WeekView from "./WeekView";
import { Star } from "@mui/icons-material";

const CustomPickersDay = ({ completedDates, allFavoriteData, ...props }) => {
  const dayString = dayjs(props.day).format("YYYY-MM-DD");
  const isCompleted = completedDates?.includes(dayString);
  const isFavorite = allFavoriteData?.includes(dayString);
  const styles = useThemeColor();
  return (
    <Box sx={{ position: "relative", display: "inline-block" }}>
      <PickersDay
        {...props}
        sx={{
          ...props.sx,
          borderRadius: isCompleted ? "50% !important" : "4px",
          border: isCompleted ? `1px solid ${styles.primaryColor}` : "none",
          "&.Mui-selected": {
            backgroundColor: `${styles.primaryColor} !important`
          }
        }}
      />

      {/* ⭐ Favorite icon (top-right) */}
      {isFavorite && (
        <Box
          sx={{
            position: "absolute",
            top: -6,
            right: -6,
            zIndex: 20,
            pointerEvents: "none" // don't block clicking the day
          }}
        >
          <Star sx={{ color: styles.yellow, fontSize: "1.5em" }} />
        </Box>
      )}
    </Box>
  );
};

const calendarStyles = (styles) => ({
  "& .MuiPaper-root": {
    backgroundColor: styles.background,
    color: "white",
    ".MuiPickersLayout-root": {
      display: "block !important",
      ".MuiPickersLayout-contentWrapper": {
        ".MuiDateCalendar-root": {
          height: "100%",
          ".MuiYearCalendar-root": {
            height: "100%",
            "& .MuiPickersYear-yearButton": {
              color: styles.color,
              borderRadius: "4px",
              "&.Mui-selected": {
                backgroundColor: styles.primaryColor,
                "&:hover": {
                  backgroundColor: styles.primaryColor
                }
              },
              "&.Mui-disabled": {
                color: styles.disabledColor
              }
            }
          },
          ".MuiPickersSlideTransition-root ": {
            minHeight: "200px",
            ".MuiPickersDay-root": {
              color: styles.color,
              borderRadius: "4px",
              "&.Mui-disabled": {
                color: styles.disabledColor
              }
            }
          }
        }
      }
    }
  },
  ".MuiPickersCalendarHeader-label": {
    color: styles.primaryColor
  },
  "& .MuiDayCalendar-weekDayLabel": {
    color: styles.color
  },
  "& .MuiPickersCalendarHeader-switchViewButton": {
    color: "white"
  },
  "& .MuiPickersArrowSwitcher-button": {
    color: styles.color,
    "&.Mui-disabled": {
      color: "#9e9e9e !important",
      opacity: 1
    }
  },
  "& .MuiPickersYear-root": {
    color: styles.color
  },
  "& .MuiPickersMonth-root": {
    color: styles.color,
    borderRadius: "4px",
    "&.Mui-selected": {
      backgroundColor: styles.primaryColor,
      "&:hover": {
        backgroundColor: styles.primaryColor
      }
    },
    "&.Mui-disabled": {
      color: styles.disabledColor
    }
  },
  "& .MuiYearCalendar-root": {
    "&::-webkit-scrollbar": {
      width: "4px"
    },
    "&::-webkit-scrollbar-track": {
      background: styles.background
    },
    "&::-webkit-scrollbar-thumb": {
      background: styles.primaryColor,
      borderRadius: "4px"
    }
  }
});

function ButtonField(props) {
  const {
    setOpen,
    InputProps: { ref } = {},
    inputProps: { "aria-label": ariaLabel } = {},
    currentMonthLabel,
    weekViewProps // Receive week view props here
  } = props;
  const styles = useThemeColor();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return isMobile ? (
    <Button
      ref={ref}
      disableRipple
      disableTouchRipple
      onClick={(e) => {
        if (e.defaultPrevented) {
          return;
        }
        setOpen?.((prev) => !prev);
      }}
    >
      <WeekView {...weekViewProps} />
    </Button>
  ) : (
    <Button
      sx={{
        textTransform: "none",
        color: styles.color,
        fontSize: "17px"
      }}
      ref={ref}
      endIcon={<ArrowDropDownIcon />}
      aria-label={ariaLabel}
      onClick={() => setOpen?.((prev) => !prev)}
    >
      {currentMonthLabel}
    </Button>
  );
}

function ButtonDatePicker(props) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const styles = useThemeColor();
  return (
    <DatePicker
      slots={{
        ...props.slots,
        field: ButtonField,
        day: (dayProps) => (
          <CustomPickersDay
            {...dayProps}
            completedDates={props.allCompletedDates}
            allFavoriteData={props.allFavoriteData}
          />
        ),
        // For mobile dialogs, we need to use the layout instead of actionBar
        layout: (layoutProps) => {
          const { children, ...other } = layoutProps;
          return (
            <PickersLayout
              {...other}
              // This removes the header from the dialog
              sx={{
                "& .MuiPickersLayout-contentWrapper": {
                  paddingTop: 0
                },
                "& .MuiDialogTitle-root": {
                  display: "none"
                },
                "& .MuiPickersToolbar-root": {
                  display: "none"
                }
              }}
            >
              {children}
              <Box
                sx={{
                  width: "100%",
                  borderTop: "1px solid rgba(255,255,255,0.12)",
                  marginTop: "8px"
                }}
              >
                <Button
                  onClick={() => {
                    const today = dayjs();
                    props.onChange(today);
                    setOpen(false);
                  }}
                  sx={{
                    color: styles.primaryColor,
                    width: "100%",
                    padding: "8px",
                    "&:hover": {
                      backgroundColor: "transparent"
                    }
                  }}
                >
                  Today
                </Button>
              </Box>
            </PickersLayout>
          );
        }
      }}
      slotProps={{
        ...props.slotProps,
        field: {
          setOpen,
          currentMonthLabel: props.currentMonthLabel,
          weekViewProps: props.weekViewProps
        },
        popper: {
          sx: calendarStyles(styles)
        },
        // Make sure dialog has proper layout on mobile
        dialog: {
          sx: {
            "& .MuiDialogContent-root": {
              padding: "16px 16px 0 16px"
            }
          }
        }
      }}
      {...props}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
    />
  );
}

export default function PickerWithButtonField(props) {
  const {
    date,
    setDate,
    activeDay,
    maxDate,
    setActiveDay,
    visibleDaysCount,
    setStartDay,
    allCompletedDates,
    allFavoriteData,
    setSnackbarMessage,
    setOpenSnackbar,
    currentActiveData,
    setDiaryData,
    startDay,
    handleDayClick,
    handleNext,
    handlePrevious,
    visibleDays,
    days,
    diaryData
  } = props;

  const [currentMonthLabel, setCurrentMonthLabel] = useState(
    dayjs(activeDay).format("MMMM")
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  useEffect(
    () => setCurrentMonthLabel(dayjs(activeDay).format("MMMM")),
    [activeDay]
  );
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ButtonDatePicker
        label={null}
        value={date}
        maxAllowedDate={maxDate}
        allCompletedDates={allCompletedDates}
        allFavoriteData={allFavoriteData}
        currentMonthLabel={currentMonthLabel}
        shouldDisableDate={(date) => {
          const tomorrow = dayjs().add(1, "day").startOf("day");
          return (
            dayjs(date).isBefore(maxDate, "day") ||
            dayjs(date).isAfter(tomorrow, "day")
          );
        }}
        onChange={async (newValue) => {
          const selectedDate = dayjs(newValue).format("YYYY-MM-DD");
          setDate(newValue);
          setActiveDay(dayjs(newValue).format("YYYY-MM-DD"));
          setCurrentMonthLabel(dayjs(newValue).format("MMMM"));
          const currentDay = dayjs(newValue).diff(maxDate, "day");
          const pageIndex = Math.floor(currentDay / visibleDaysCount);
          if (isMobile) {
            setStartDay(pageIndex * visibleDaysCount + 1);
          } else {
            const selectedIndex = days.findIndex(
              (day) => dayjs(day.value).format("YYYY-MM-DD") === selectedDate
            );

            if (selectedIndex !== -1) {
              // If we're on mobile, simply set the start day to the selected index + 1

              // For desktop, we want to show the week containing the selected date
              // Find which day of the week it is (0 = Sunday, 6 = Saturday)
              const dayOfWeek = dayjs(newValue).day();

              // Calculate the start of the week (Sunday)
              const startOfWeekIndex = selectedIndex - dayOfWeek;

              // Make sure we don't go below 0
              const newStartDay = Math.max(1, startOfWeekIndex + 1);

              setStartDay(newStartDay);
            } else {
              // Fallback if the date is not found in the days array
              console.warn("Selected date not found in days array");
            }
          }
        }}
        weekViewProps={{
          allCompletedData: allCompletedDates,
          allFavoriteData: allFavoriteData,
          setOpenSnackbar,
          setSnackbarMessage,
          currentActiveData,
          setDiaryData,
          activeDay,
          setActiveDay,
          startDay,
          visibleDays,
          visibleDaysCount,
          days,
          setStartDay,
          diaryData,
          handleDayClick,
          handleNext,
          handlePrevious
        }}
      />
    </LocalizationProvider>
  );
}
